import { buildReport } from './scenario';
import { round1 } from './stats';
import type { HarnessReport } from './scenario';
import type { RunRecord } from '../types';
import { copyArtifactText, downloadArtifact, shareArtifact, stampedFilename } from './shareOut';
import type { Artifact, ExportRoute } from './shareOut';

/**
 * The U-0 latency bundle: what gets exported, and how it reads.
 *
 * The three escape routes themselves — Web Share file, Web Share text, download/clipboard, each
 * failing loudly — live in `shareOut.ts` and are shared with U-1. This file owns only the U-0
 * *content*: the schema, and the plain-text summary that leads with ENV-1 admissibility, because a
 * number without it is a number that will eventually be quoted as evidence it is not.
 */

export type { ExportRoute } from './shareOut';

export interface ExportBundle {
  schema: 'touchline.u0.harness';
  version: 1;
  generatedAt: string;
  /**
   * The bundle that generated this file. Per-run stamps live on `runs[].environment.buildId`; this
   * is the exporting build, which is the same thing unless the file mixes runs across an update.
   */
  buildId: string;
  /** The verdicts, so the file is readable without re-running the analysis. */
  report: HarnessReport;
  /** Every raw sample, so the analysis can be redone or disputed. */
  runs: RunRecord[];
}

export function buildBundle(runs: readonly RunRecord[], generatedAt: number): ExportBundle {
  return {
    schema: 'touchline.u0.harness',
    version: 1,
    generatedAt: new Date(generatedAt).toISOString(),
    buildId: __BUILD_ID__,
    report: buildReport(runs, generatedAt),
    runs: [...runs],
  };
}

export function bundleToJson(bundle: ExportBundle): string {
  return JSON.stringify(bundle, null, 2);
}

/**
 * The plain-text summary — what gets pasted into a message when JSON is more than the moment needs.
 * Deliberately includes the ENV-1 admissibility line first.
 */
export function bundleToText(bundle: ExportBundle): string {
  const lines: string[] = [];
  lines.push('TOUCHLINE — build unit U-0 — tap→commit latency');
  lines.push(bundle.generatedAt);
  lines.push(`build ${bundle.buildId}`);
  lines.push('');

  const gate = bundle.report.gate;
  lines.push(
    gate.admissible
      ? 'ENV-1: ADMISSIBLE — measured on the target device as an installed PWA.'
      : 'ENV-1: NOT ACCEPTANCE EVIDENCE.',
  );
  for (const blocker of gate.blockers) lines.push(`  ! ${blocker}`);
  for (const warning of gate.warnings) lines.push(`  ~ ${warning}`);
  lines.push('');

  lines.push(`A-1: ${bundle.report.a1.headline}`);
  lines.push(bundle.report.a1.detail);
  lines.push('');
  lines.push(`F-9: ${bundle.report.f9.headline}`);
  lines.push(bundle.report.f9.detail);
  lines.push('');

  if (bundle.report.excluded.length > 0) {
    lines.push('── Excluded from the aggregate verdicts (kept, not deleted)');
    for (const x of bundle.report.excluded) {
      lines.push(`   ${x.runId} (${x.condition}) — ${x.reason}`);
    }
    lines.push('');
  }

  // Stated before the per-condition numbers, because it is what makes them readable. Without it,
  // a synthetic tap→ack of 17 ms and a real one of 41 ms sit side by side looking comparable.
  const basis = bundle.report.nfr1Basis;
  lines.push(`── NFR-1 basis: ${basis.basis}`);
  lines.push(`   ${basis.note}`);
  if (basis.realInputP50Ms !== null) {
    lines.push(
      `   touch delivery (real taps only): p50 ${round1(basis.realInputP50Ms)} ms — auto runs report 0 here by construction`,
    );
  }
  lines.push('');

  for (const c of bundle.report.conditions) {
    lines.push(
      `── ${c.condition.toUpperCase()} · ${c.mode === 'liveTap' ? 'REAL TAPS' : 'synthetic'} — n=${c.sampleCount}, failures=${c.failureCount}`,
    );
    if (c.mode === 'liveTap') {
      lines.push(
        `   input   p50 ${round1(c.input.p50)}  p95 ${round1(c.input.p95)}  max ${round1(c.input.max)} ms  (pointerup → dispatch)`,
      );
    }
    if (c.provenance === 'unknown') {
      lines.push(
        '   !! PROVENANCE UNKNOWN — recorded before the harness checked for overlapping runs; excluded from all aggregate verdicts',
      );
    } else if (c.foreignHeatWorkersDuringRun !== null && c.foreignHeatWorkersDuringRun > 0) {
      lines.push(
        `   !! CONTAMINATED — ${c.foreignHeatWorkersDuringRun} heat worker(s) from another run were live during sampling`,
      );
    }
    lines.push(
      `   commit  p50 ${round1(c.commit.p50)}  p95 ${round1(c.commit.p95)}  p99 ${round1(c.commit.p99)}  max ${round1(c.commit.max)} ms`,
    );
    lines.push(
      `   tap→ack p50 ${round1(c.total.p50)}  p95 ${round1(c.total.p95)}  p99 ${round1(c.total.p99)}  max ${round1(c.total.max)} ms`,
    );
    lines.push(
      `   over 60 ms commit: ${c.overCommitBudget.count} (${(c.overCommitBudget.fraction * 100).toFixed(1)}%)  ·  over 100 ms ack: ${c.overNfr1Budget.count} (${(c.overNfr1Budget.fraction * 100).toFixed(1)}%)`,
    );
    for (const check of c.checks) {
      lines.push(
        `   ${check.verdict.toUpperCase().padEnd(12)} ${check.id} — ${round1(check.observedMs)} ms vs ${check.budgetMs} ms`,
      );
    }
    lines.push('');
  }

  const storage = bundle.runs[0]?.storage;
  if (storage) {
    lines.push('── BAK-3 storage probe');
    lines.push(
      `   usage ${storage.usageBytes ?? '—'} B of quota ${storage.quotaBytes ?? '—'} B · persisted=${String(storage.persistedAfter)}`,
    );
    lines.push('');
  }

  const env = bundle.runs[0]?.environment;
  if (env) {
    lines.push('── Device');
    lines.push(`   build ${env.buildId}`);
    lines.push(`   ${env.userAgent}`);
    lines.push(
      `   standalone=${String(env.standalone)} · ${env.screenW}×${env.screenH} @${env.devicePixelRatio}x · cores=${env.hardwareConcurrency ?? '—'} · timer=${env.timerResolutionMs} ms`,
    );
  }

  return lines.join('\n');
}

function toArtifact(bundle: ExportBundle): Artifact {
  return {
    json: bundleToJson(bundle),
    text: bundleToText(bundle),
    filename: stampedFilename('touchline-u0', Date.parse(bundle.generatedAt)),
    title: 'Touchline U-0 results',
  };
}

/**
 * @throws {ExportError} when every route fails, or when the share sheet is cancelled. The caller
 *   shows the message on screen — a venue run must never end with the user believing results were
 *   shared when they were not.
 */
export async function shareBundle(bundle: ExportBundle): Promise<ExportRoute> {
  return shareArtifact(toArtifact(bundle));
}

/** @throws {ExportError} if the object URL or anchor click fails. */
export function downloadBundle(bundle: ExportBundle): void {
  downloadArtifact(toArtifact(bundle));
}

/** @throws {ExportError} when the clipboard is unavailable or refuses. */
export async function copyBundleText(bundle: ExportBundle): Promise<void> {
  await copyArtifactText(toArtifact(bundle));
}
